/*
  «Выбрать весь отбор» - решение владельца 23.10.2026.

  С пагинацией по десять флажок «выбрать все» в шапке таблицы берёт только видимую страницу
  (раньше в реестре - до 50 строк, в обзвоне - до 500, в очереди - всё набранное). Владелец
  выбрал вторую кнопку рядом с таблицей: она спрашивает у сервера строки ВСЕГО отбора - тем
  же запросом, что и страница, с нынешними фильтром и поиском - и добавляет их к выбору.
  Отметки, набранные раньше, остаются: так же ведёт себя и флажок страницы.

  Сервер отдаёт не больше, чем принимает одно действие над выбранными (`ceiling`), и
  говорит, сколько подошло (`total`). Взято меньше, чем подошло, - кнопка говорит это словами:
  человек, уверенный, что выбрал всё, а выбравший первую тысячу, отправил бы остальных
  никуда.

  Кнопка видна, когда отбор длиннее показанной страницы: иначе флажок в шапке и так берёт
  всё. Итог выбора сбрасывается со сменой отбора (`filterKey`): «выбрано 300 из 300» прежнего
  отбора про новый ничего не говорит. Ответ, пришедший после смены отбора, отбрасывается -
  иначе к выбору добавились бы строки, которых в нынешнем отборе нет.
*/

import { useEffect, useRef, useState } from 'react'

import { Notice } from './Common.jsx'

const IDLE = { busy: false, error: null, done: null }

/*
  `now` - сколько отмечено СЕЙЧАС (аудит 26.10.2026, причина 7): итог нажатия устаревает, как
  только человек снял отметки, и подпись «Выбрано 295 из 295» висела над пустым выбором.
  Без `now` (вызов без счётчика) остаётся число из ответа сервера.
*/
function outcome({ taken, total, ceiling }, now) {
  const count = now ?? taken
  if (taken >= total) return `Выбрано ${count} из ${total}.`
  return (
    `Выбрано ${count} из ${total}: за одно действие - не больше ${ceiling}, взяты первые по ` +
    'порядку таблицы. Сузьте отбор, чтобы выбрать остальные.'
  )
}

export default function PickWhole({ total, shown, filterKey, fetchAll, onPicked, selected }) {
  const [state, setState] = useState(IDLE)
  const [seen, setSeen] = useState(filterKey)
  const current = useRef(filterKey)
  useEffect(() => {
    current.current = filterKey
  }, [filterKey])
  if (seen !== filterKey) {
    setSeen(filterKey)
    setState(IDLE)
  }

  async function pick() {
    const asked = filterKey
    setState({ busy: true, error: null, done: null })
    try {
      const answer = await fetchAll()
      if (current.current !== asked) return
      onPicked(answer.items)
      setState({
        busy: false,
        error: null,
        done: { taken: answer.items.length, total: answer.total, ceiling: answer.ceiling },
      })
    } catch (error) {
      if (current.current === asked) setState({ busy: false, error, done: null })
    }
  }

  if (!(total > shown)) return null
  return (
    <>
      <div className="row">
        <button type="button" className="button button--quiet" disabled={state.busy} onClick={pick}>
          {state.busy ? 'Выбираем…' : `Выбрать весь отбор: ${total}`}
        </button>
        <span className="muted">
          {state.done && selected !== 0
            ? outcome(state.done, selected)
            : '«Выбрать показанные» берёт только показанную страницу.'}
        </span>
      </div>
      <Notice error={state.error} />
    </>
  )
}
